import { useEffect, useMemo, useState } from "react";
import {
  getDocumentOverview,
  getEmployeeDocumentsForAdmin,
  requestEmployeeDocument,
  reviewEmployeeDocument,
} from "../api/instateamAdminApi";
import { AppIcon, MetricCard } from "../components/InstateamMasterComponents";
import { API_BASE_URL, toAbsoluteUrl } from "../utils/adminHelpers";

const INITIAL_REQUEST_FORM = {
  documentLabel: "",
  note: "",
};

function formatDate(value) {
  if (!value) return "Not uploaded";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not uploaded";
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function statusTone(status) {
  return {
    verified: "active",
    pending: "pending",
    requested: "warning",
    rejected: "inactive",
    not_uploaded: "muted",
  }[status] || "muted";
}

function statusLabel(status) {
  return String(status || "not_uploaded")
    .split("_")
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
}

function getDownloadFileName(file, fallbackName) {
  const originalName = String(file?.originalName || "").trim();
  if (originalName) return originalName;
  return `${String(fallbackName || "document").replace(/[\\/:*?"<>|]+/g, "-")}.pdf`;
}

export default function DocumentsPage({
  token,
  departmentOptions = [],
  searchText: headerSearchText,
  onSearchChange,
  onProtectedError,
  onNotify,
}) {
  const [employees, setEmployees] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState(null);
  const [localSearchText, setLocalSearchText] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [requestForm, setRequestForm] = useState(INITIAL_REQUEST_FORM);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const searchText = headerSearchText ?? localSearchText;
  const setSearchText = onSearchChange || setLocalSearchText;

  async function fetchOverview() {
    if (!token) return;

    try {
      setIsLoading(true);
      const payload = await getDocumentOverview(token, {
        search: searchText,
        department: departmentFilter,
        status: statusFilter,
      });
      setEmployees(payload.employees || []);
      setMetrics(payload.metrics || null);
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function openEmployee(employeeId) {
    try {
      setIsLoading(true);
      const payload = await getEmployeeDocumentsForAdmin(token, employeeId);
      setSelectedEmployee(payload.employee || null);
      setSelectedDocuments(payload.documents || []);
      setSelectedMetrics(payload.metrics || null);
      setRequestForm(INITIAL_REQUEST_FORM);
      setShowRequestForm(false);
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, searchText, departmentFilter, statusFilter]);

  useEffect(() => {
    if (!token) return undefined;

    const refreshDocuments = () => {
      if (document.hidden) return;
      if (selectedEmployee?._id && !showRequestForm) {
        openEmployee(selectedEmployee._id);
      } else {
        fetchOverview();
      }
    };

    const intervalId = window.setInterval(refreshDocuments, 5000);
    window.addEventListener("focus", refreshDocuments);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshDocuments);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedEmployee?._id, showRequestForm, searchText, departmentFilter, statusFilter]);

  const metricCards = useMemo(() => {
    const source = selectedEmployee ? selectedMetrics : metrics;
    return [
      { label: "Total Documents", value: source?.total || 0, helper: "Tracked records", icon: "documents", tone: "yellow" },
      { label: "Verified", value: source?.verified || 0, helper: "Approved documents", icon: "activeUsers", tone: "green" },
      { label: "Pending", value: source?.pending || 0, helper: "Waiting review", icon: "calendar", tone: "blue" },
      { label: "Rejected", value: source?.rejected || 0, helper: "Needs re-upload", icon: "complaints", tone: "red" },
    ];
  }, [metrics, selectedEmployee, selectedMetrics]);

  async function handleRequestDocument(event) {
    event.preventDefault();
    if (!selectedEmployee?._id) return;

    try {
      setIsSaving(true);
      await requestEmployeeDocument(token, selectedEmployee._id, requestForm);
      onNotify?.({ type: "success", text: "Document requested from employee." });
      await openEmployee(selectedEmployee._id);
      await fetchOverview();
      setShowRequestForm(false);
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    } finally {
      setIsSaving(false);
    }
  }

  async function handleReview(document, status) {
    if (document.source !== "request") return;
    const rejectionReason = status === "rejected"
      ? window.prompt("Enter rejection reason")
      : "";
    if (status === "rejected" && !rejectionReason) return;

    try {
      await reviewEmployeeDocument(token, document._id, { status, rejectionReason });
      onNotify?.({ type: "success", text: `Document ${status}.` });
      await openEmployee(selectedEmployee._id);
      await fetchOverview();
    } catch (error) {
      if (!onProtectedError?.(error)) {
        onNotify?.({ type: "error", text: error.message });
      }
    }
  }

  async function handleDownload(document) {
    if (!document.file?.url || !selectedEmployee?._id) return;

    try {
      const searchParams = new URLSearchParams({
        source: document.source || "profile",
        documentKey: document.documentKey || "",
        requestId: document.source === "request" ? document._id : "",
      });
      const response = await fetch(`${API_BASE_URL}/instateam/documents/admin/employees/${selectedEmployee._id}/download?${searchParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error("Unable to download document.");
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = getDownloadFileName(document.file, document.documentLabel);
      window.document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      onNotify?.({ type: "error", text: error.message });
    }
  }

  return (
    <section className="documents-page">
      <div className="page-action-row">
        <div>
          <h3>{selectedEmployee ? selectedEmployee.fullName : "Documents"}</h3>
          <p>{selectedEmployee ? `${selectedEmployee.employeeId} | ${selectedEmployee.department}` : "Track employee uploads, requests, and verification status."}</p>
        </div>
        {selectedEmployee ? (
          <div className="page-actions">
            <button className="secondary-action" type="button" onClick={() => setSelectedEmployee(null)}>
              <AppIcon name="chevronLeft" />
              Back
            </button>
            <button className="primary-action" type="button" onClick={() => setShowRequestForm((value) => !value)}>
              <AppIcon name="plus" />
              Request Document
            </button>
          </div>
        ) : null}
      </div>

      <div className="metrics-grid">
        {metricCards.map((metric) => <MetricCard key={metric.label} {...metric} />)}
      </div>

      {!selectedEmployee ? (
        <article className="data-card">
          <div className="data-card-header document-filter-header">
            <div className="inline-search">
              <AppIcon name="search" />
              <input value={searchText} onChange={(event) => setSearchText(event.target.value)} placeholder="Search employee name, ID..." />
            </div>
            <select value={departmentFilter} onChange={(event) => setDepartmentFilter(event.target.value)} className="document-select">
              <option value="all">All Departments</option>
              {departmentOptions.map((department) => <option key={department} value={department}>{department}</option>)}
            </select>
            <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className="document-select">
              <option value="all">All Status</option>
              <option value="verified">Verified</option>
              <option value="pending">Pending</option>
              <option value="requested">Requested</option>
              <option value="rejected">Rejected</option>
              <option value="not_uploaded">Not Uploaded</option>
            </select>
          </div>

          <div className="responsive-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Employee ID</th>
                  <th>Department</th>
                  <th>Total</th>
                  <th>Verified</th>
                  <th>Pending</th>
                  <th>Rejected</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => (
                  <tr key={employee._id}>
                    <td><strong>{employee.fullName}</strong></td>
                    <td>{employee.employeeId}</td>
                    <td>{employee.department}</td>
                    <td>{employee.metrics.total}</td>
                    <td>{employee.metrics.verified}</td>
                    <td>{employee.metrics.pending + employee.metrics.requested}</td>
                    <td>{employee.metrics.rejected}</td>
                    <td>
                      <button className="secondary-action small-action" type="button" onClick={() => openEmployee(employee._id)}>
                        <AppIcon name="eye" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
                {!employees.length ? (
                  <tr><td className="empty-table-state" colSpan="8">{isLoading ? "Loading documents..." : "No employees found."}</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </article>
      ) : (
        <div className="document-detail-stack">
          <article className="data-card document-employee-hero">
            <div>
              <h3>{selectedEmployee.fullName}</h3>
              <p>{selectedEmployee.employeeId} | {selectedEmployee.department} | {selectedEmployee.designation}</p>
              <p>{selectedEmployee.email || ""} {selectedEmployee.phoneNumber ? `| ${selectedEmployee.phoneNumber}` : ""}</p>
            </div>
          </article>

          {showRequestForm ? (
            <form className="data-card document-request-inline-card" onSubmit={handleRequestDocument}>
              <label className="form-field">
                <span>Document Name</span>
                <div className="field-shell">
                  <input value={requestForm.documentLabel} onChange={(event) => setRequestForm((current) => ({ ...current, documentLabel: event.target.value }))} placeholder="Degree Certificate" />
                </div>
              </label>
              <label className="form-field">
                <span>Instructions</span>
                <div className="field-shell">
                  <input value={requestForm.note} onChange={(event) => setRequestForm((current) => ({ ...current, note: event.target.value }))} placeholder="Please upload the latest clear copy." />
                </div>
              </label>
              <button className="primary-action" type="submit" disabled={isSaving}>{isSaving ? "Requesting..." : "Send Request"}</button>
            </form>
          ) : null}

          <article className="data-card">
            <div className="data-card-header">
              <div>
                <h3>Employee Documents</h3>
                <p>View, verify, reject, and track requested documents.</p>
              </div>
            </div>
            <div className="responsive-table">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>File</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th>Action</th>
                    <th>Download</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedDocuments.map((document) => (
                    <tr key={document._id}>
                      <td>
                        <strong>{document.documentLabel}</strong>
                        {document.note ? <small className="document-note">{document.note}</small> : null}
                        {document.rejectionReason ? <small className="document-note danger">{document.rejectionReason}</small> : null}
                      </td>
                      <td>{document.file?.originalName || "No file uploaded"}</td>
                      <td>{formatDate(document.uploadedAt)}</td>
                      <td><span className={`status-badge ${statusTone(document.status)}`}>{statusLabel(document.status)}</span></td>
                      <td>
                        <div className="table-actions">
                          {document.file?.url ? (
                            <>
                              <a className="secondary-action small-action" href={toAbsoluteUrl(document.file.url)} target="_blank" rel="noreferrer">
                                <AppIcon name="eye" />
                              </a>
                            </>
                          ) : null}
                          {document.source === "request" && document.status === "pending" ? (
                            <>
                              <button type="button" onClick={() => handleReview(document, "verified")}><AppIcon name="activeUsers" /></button>
                              <button type="button" onClick={() => handleReview(document, "rejected")}><AppIcon name="close" /></button>
                            </>
                          ) : null}
                        </div>
                      </td>
                      <td>
                        {document.file?.url ? (
                          <button
                            type="button"
                            className="secondary-action small-action document-download-action"
                            onClick={() => handleDownload(document)}
                            aria-label={`Download ${document.documentLabel}`}
                          >
                            <AppIcon name="export" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </article>

        </div>
      )}
    </section>
  );
}
